import { FC, Props } from "./typings";
import styles from "./styles.module.scss";
import { useContext, useEffect, useRef, useState } from "react";
import { InModalContext } from "contexts/InModalContext";
import tagsService from "api/tagsService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CloseIcon from "assets/icons/close.svg?react";
import { getCompanyId as getWorkspaceId } from "utils/getCompanyId";
import { getCurrentInputAppearance, muiVariant } from "utils/inputAppearance";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import classNames from "classnames";
import { isValidEmail } from "utils/isValidEmail";
import { isValidNip } from "utils/IsValidNip";
import { isValidPhoneNumber } from "utils/isValidPhoneNumber";
import { isValidBankAccount } from "utils/isValidBankAccount";
import { formatToFormat } from "utils/formatToFormat";
import Chip from "components/Chip";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";

const Input: FC<Props> = ({
  id,
  type = "input",
  autoComplete = "off",
  onChooseTag = () => {},
  label,
  labelPosition = "top",

  onChange,
  defaultValue,
  value,
  disabledTags = false,
  tagType,
  onBlur,
  onFocus,
  onRightClick,
  onRightText,
  hideTags = false,
  focus = false,
  isPhoneNumber = false,
  onAccept,
  // Wariant przekazany jawnie przez rodzica (nadpisuje logikę per-modal poniżej).
  variant,
  isEmail,
  isNip,
  isBank,
  isCode,
  Icon,
  LeftIcon,
  RightIcon,
  elements,
  chips,
  onChipDelete,
  disabled = false,
  disableBorder = false,
  allowToShowPassword = false,
  hideClearButton = false,

  noBackground = false,
  inputProps,
  size = "small",
  customBg,
}) => {
  const { t } = useTranslation();
  // Wariant pola z preferencji „Wygląd pól" TYLKO w oknie modalnym; poza modalem bazowy
  // (wypełniony), żeby zmiana ustawienia nie wyciekała na pola nad gridami/paskach narzędzi.
  const inModal = useContext(InModalContext);
  const resolvedVariant =
    variant ??
    (inModal ? muiVariant(getCurrentInputAppearance().variant) : "filled");
  const ref = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [text, setText] = useState(defaultValue ?? "");

  const currentWorkspaceId = getWorkspaceId();
  tagsService.useGetTags(
    disabledTags || !tagType,
    tagType,
    undefined,
    currentWorkspaceId ?? "",
  );

  const [showTags, setShowTags] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const v = e.target.value;

    setText(v);
    onChange(v);
  };

  useEffect(() => {
    const formatedText = value ?? text;

    const isHashtag = formatedText.search("#") !== -1;

    if (isHashtag && !showTags && !hideTags) {
      setShowTags(true);
      return;
    }

    if (!isHashtag && showTags) {
      setShowTags(false);
      return;
    }
  }, [value, text]);

  const handleClickButton = (e: any) => {
    if (e.key === "Enter" && isFocused) {
      if (onAccept) {
        onAccept?.();
        return;
      }
      const formatedText = value ?? text;
      if (formatedText.search("#") === -1) return;
      const tag = formatedText.split("#")[1].replaceAll("_", " ");

      setShowTags(false);

      onChooseTag({
        Id: "-1",
        Name: tag,
        Type: 2,
        Flags: 0,
        Global: false,
      });

      setText(formatedText.split("#")[0]);
      onChange(formatedText.split("#")[0]);
    }
  };

  const handleChoose = () => {
    //@ts-ignore
    ref?.current?.focus();
  };

  useEffect(() => {
    if (!ref?.current || !focus) return;
    handleChoose();
  }, [ref, focus]);

  useEffect(() => {
    document.addEventListener("keypress", handleClickButton);

    return () => document.removeEventListener("keypress", handleClickButton);
  }, [value, text, isFocused]);

  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  const getEndAndorment = () => {
    if (elements) {
      return elements;
    }

    if (onRightClick) {
      return (
        <>
          {value?.length > 0 && !disabled && !hideClearButton && (
            <CloseIcon
              className={classNames("cursor-pointer", styles.closeIcon)}
              onClick={() => {
                setText("");
                onChange("");
              }}
            />
          )}
          <div
            className={`${styles.clk} ${styles.button}`}
            onClick={onRightClick}
          >
            {onRightText}
          </div>
        </>
      );
    }

    if (RightIcon) {
      return RightIcon;
    }

    if (value?.length && !disabled && !hideClearButton) {
      return (
        <CloseIcon
          className={classNames("cursor-pointer", styles.closeIcon)}
          onClick={() => {
            setText("");
            onChange("");
          }}
        />
      );
    }
  };

  const getStartAdornment = () => {
    if (chips?.length) {
      return (
        <div className="flex gap-3">
          {chips.map((chip) => {
            return (
              <Chip
                element={<>{chip.label}</>}
                onDelete={() => onChipDelete?.(chip.id)}
                small
              />
            );
          })}
        </div>
      );
    }

    if (Icon) {
      return (
        <InputAdornment position="start">
          {<Icon className={styles.startIcon} />}
        </InputAdornment>
      );
    }

    if (LeftIcon) {
      return LeftIcon;
    }

    return undefined;
  };

  const getInputProps = () => {
    return {
      startAdornment:
        getStartAdornment() || labelPosition === "start" ? (
          <InputAdornment style={{}} position="start">
            {getStartAdornment()}
            {labelPosition === "start" && (
              <div
                className={`${styles.startAdornment} ${
                  !Icon ? styles.withoutIcon : ""
                }`}
              >
                {label}
              </div>
            )}
          </InputAdornment>
        ) : undefined,

      endAdornment: (
        <InputAdornment position="end">
          {type === "password" && allowToShowPassword && (
            <IconButton
              aria-label={
                showPassword ? t("auth.hidePassword") : t("auth.showPassword")
              }
              onClick={handleClickShowPassword}
              onMouseDown={handleMouseDownPassword}
              onMouseUp={handleMouseUpPassword}
              edge="end"
              style={{ marginRight: "2px" }}
            >
              {showPassword ? (
                <FontAwesomeIcon
                  icon={faEyeSlash}
                  className={styles.showPassIcon}
                />
              ) : (
                <FontAwesomeIcon icon={faEye} className={styles.showPassIcon} />
              )}
            </IconButton>
          )}
          {getEndAndorment()}
        </InputAdornment>
      ),
      disableUnderline: disableBorder ? true : false,
      readOnly: disabled,
    };
  };

  useEffect(() => {}, [value]);

  const isValid = () => {
    if (!value?.length) return true;

    if (isEmail && !isValidEmail(value)) {
      return false;
    }

    if (isNip && !isValidNip(value)) {
      return false;
    }

    if (isPhoneNumber && !isValidPhoneNumber(value)) {
      return false;
    }

    if (isBank && !isValidBankAccount(value)) {
      return false;
    }

    return true;
  };

  function isLetter(char) {
    return /[a-zA-Z]/.test(char);
  }

  const format = () => {
    if (isPhoneNumber) {
      return formatToFormat(value, "### ### ###");
    }

    if (isNip) {
      return formatToFormat(value, "### ### ## ##");
    }

    if (isBank) {
      let format = "## #### #### #### #### #### ####";
      if (isLetter(value?.charAt(0))) {
        format = "## ## #### #### #### #### #### ####";
      }
      return formatToFormat(value, format);
    }

    if (isCode) {
      // Polski kod pocztowy: XX-XXX (2 cyfry, myślnik, 3 cyfry).
      return formatToFormat(value, "##-###");
    }

    return value;
  };

  return (
    <TextField
      autoComplete={autoComplete}
      value={format()}
      size={size}
      id={id}
      label={labelPosition === "top" ? label : null}
      error={!isValid()}
      InputProps={getInputProps()}
      inputProps={inputProps}
      onFocus={() => {
        setIsFocused(true);
        onFocus?.();
      }}
      inputRef={ref}
      onBlur={() => {
        setIsFocused(false);
        onBlur?.();
      }}
      onChange={(e) => handleChange(e)}
      style={{
        width: "100%",
        // height: height,
      }}
      sx={{
        // Class for the label of the input field
        "&:hover:not(.Mui-focused)": {
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#5d49c7",
          },
        },
        "& .MuiInputBase-input": {
          paddingBottom:
            resolvedVariant !== "outlined" ? "0px !important" : undefined,
        },
        "& .MuiInputBase-root": {
          backgroundColor: noBackground ? "transparent !important" : customBg,
        },
        "& .MuiInputBase-readOnly": {
          backgroundColor: "#f4eff4 !important",
          color: "#a29ea3",
        },
      }}
      className={`${styles.t} app-input`}
      variant={resolvedVariant}
      color={"primary"}
      type={type === "password" && !showPassword ? "password" : "text"}
    />
  );
};

export default Input;
